// Package imports:
import React, { useContext, useState } from 'react';
import { format } from "date-fns";
// Component imports:
// import Button from '../../ui-elements/Button/Button';
import LmdTable from '../../ui-elements/Table/LmdTable';
import SmallSearch from '../SmallSearch/SmallSearch';
import DisplayBox from '../DisplayBox/DisplayBox';
import HeadSection from '../../ui-elements/HeadSection/HeadSection';
import Tooltip from '../../ui-elements/Tooltip/Tooltip';
// Service imports:
import { useApiLmdData, useApiLmdDataMappedByString } from '../../services/apiHooks';
import { icelandifyString } from '../../services/utils';
// Context imports:
import { AccessTokenContext } from '../../contexts/AccessTokenContext';

interface ILeigusamningur {
    Leigusamningur: number,
    Emnr: number,
    Skjalanumer: string,
    Fastnum: number,
    Heinum: number,
    Heimilisfang: string,
    Svfn: number,
    Sveitarfelag: string,
    Utgdag: string,
    Thinglystdags: string,
    Dagsfra: string,
    Dagstil: string,
    Heildarverd: number,
    Staerd: number,
    FjHerbergi: number,
    Otimabundid: boolean,
    Visitala: string,
    DagsVisitala: null,
    Tegund: string,
    OnothaefurSamningur: boolean,
    Inserted: string,
    Updated: null
}


function numberWithDots(num: string) {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

const Leigusamningar: React.FC = () => {
    const accessToken = useContext(AccessTokenContext);

    const [ latestLeigusamningar ] = useApiLmdData<ILeigusamningur[]>(`/static_data/v1/register/residental_rent/latest?limit=50`, accessToken);
    const [ mostRecentlyFetchedLeigusamningar, setMostRecentlyFetchedLeigusamningar ] = useState<ILeigusamningur[]>([]);
    const [ searchInput, setSearchInput, leigusamningarForSearchInput ] = useApiLmdDataMappedByString<ILeigusamningur[]>(
        '',
        (searchInput: string) => `/v1/static_data/v1/register/residental_rent/address/${searchInput}*/?limit=50`,
        accessToken,
        undefined,
        undefined,
        (leigusamningar) => setMostRecentlyFetchedLeigusamningar(leigusamningar)
    );

    const Leigusamningar =
        (searchInput.length < 3)
        ? latestLeigusamningar
        : (leigusamningarForSearchInput.data === null && leigusamningarForSearchInput.error === null)
        ? ({
            data: mostRecentlyFetchedLeigusamningar,
            error: null
        })
        : leigusamningarForSearchInput;

    return (
        <div className='main KCL_kaupsamningar KCL_leigusamningar'>
            <div className="section section--primary">
                <div className='section__body'>
                    <div className='shell'>
                        <HeadSection
                            title='Leigusamningar'
                            description={
                                <p>
                                    Hér má finna leigusamninga frá HMS. HMS skráir upplýsingarnar upp úr þinglýstum leigusamningum.<br/>
                                    Leigusamningar geta verið fyrir íbúðarhúsnæði, sumarhús eða atvinnuhúsnæði.
                                </p>
                            }
                        />

                        <div className='row'>
                            <div className="col-lg-7 col-sm-12">
                                <div className="search__box">
                                    <SmallSearch
                                        search={searchInput}
                                        setSearch={setSearchInput}
                                        placeHolder="Leitaðu eftir heimilisfangi..."
                                        id="Search_Leigusamningar"
                                    />
                                    <p className="info">Byggt á upplýsingum frá HMS frá 2006-{new Date().getFullYear()}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="section--gray">
                        <div className="shell">
                            <DisplayBox>
                                <LmdTable
                                    tableSize='lg'
                                    apiData={Leigusamningar}
                                    columns={[
                                        {
                                            title: 'Fastanúmer',
                                            dataAccessor: ({ Fastnum }) => Fastnum,
                                            textAlign: 'left',
                                            simpleSortable: ({ Fastnum }) => Fastnum
                                        },
                                        {
                                            title: "Heimilisfang",
                                            dataAccessor: ({ Heimilisfang }) => (
                                                Heimilisfang
                                                // <Link
                                                //     url={`https://keldan.is/Property/PropertySearch?term=${Heimilisfang}`}
                                                //     linkSize='15'
                                                //     targetBlank
                                                // >
                                                //     {Heimilisfang}
                                                // </Link>
                                            ),
                                            textAlign: 'left',
                                            simpleSortable: ({ Heimilisfang }) => Heimilisfang
                                        },
                                        {
                                            title: "Tegund",
                                            dataAccessor: ({ Tegund }) => icelandifyString(Tegund),
                                            textAlign: 'left',
                                            simpleSortable: ({ Tegund }) => icelandifyString(Tegund)
                                        },
                                        {
                                            title: "Dags . Þingl.",
                                            dataAccessor: ({ Thinglystdags }) => format(new Date(Thinglystdags.replace(/ /g, "T")), "dd.MM.yyyy"),
                                            textAlign: 'right',
                                            simpleSortable: ({ Thinglystdags }) => Thinglystdags
                                        },
                                        {
                                            title: "Heildarverð",
                                            dataAccessor: ({ Heildarverd }) => numberWithDots(Heildarverd.toFixed(0)) + " kr.",
                                            textAlign: 'right',
                                            simpleSortable: ({ Heildarverd }) => Heildarverd
                                        },
                                        {
                                            title: "Stærð",
                                            dataAccessor: ({ Staerd }) => {
                                                return <>{Staerd}{" m"}<sup>2</sup></>
                                            },
                                            textAlign: 'right',
                                            simpleSortable: ({ Staerd }) => Staerd
                                        },
                                        {
                                            title: "Fermetraverð",
                                            dataAccessor: ({ Heildarverd, Staerd }) => {
                                                return <>{numberWithDots((Heildarverd / Staerd).toFixed(0))} kr.</>
                                            },
                                            textAlign: 'right',
                                            simpleSortable: ({ Heildarverd, Staerd }) => (Heildarverd / Staerd)
                                        },
                                        {
                                            title: (
                                                <Tooltip
                                                    text='Nothæfur samn.'
                                                    tooltip={
                                                        <span>
                                                            Samningur verður ónothæfur í vísitöluútreikning og<br/>
                                                            samanburð t.d. vegna þess að samningurinn er<br/>
                                                            - Milli skyldra<br/>
                                                            - Fjöldi fasteigna í samningi fleiri en 1<br/>
                                                            - Kvöð um notkun<br/>
                                                            - Fjármálastofnun selur<br/>
                                                            - Fjármálastofnun kaupir<br/>
                                                            - Greitt með lausafé<br/>
                                                            - Greitt með fasteign<br/>
                                                            - Selt að hluta<br/>
                                                            - Ófullnægjandi uppl. um staðgreiðsluverð<br/>
                                                            - Ástandi ábótavant<br/>
                                                        </span>
                                                    }
                                                />
                                            ),
                                            dataAccessor: ({ OnothaefurSamningur }) => OnothaefurSamningur ? "Nei" : "Já",
                                            textAlign: 'center',
                                            simpleSortable: ({ OnothaefurSamningur }) => (OnothaefurSamningur ? "Nei" : "Já")
                                        }/*, {
                                            title: "Verðmat",
                                            dataAccessor: ({ Fastnum }) => (
                                                <Button
                                                    size='sm'
                                                    buttonType='secondary'
                                                    anchorProps={{
                                                        target: '_blank',
                                                        href: `/Fasteignir/Leit?fastanr=${Fastnum}#Gangverd`
                                                    }}
                                                >
                                                    Verðvísir
                                                </Button>
                                            ),
                                            textAlign: 'left',
                                        }*/
                                    ]}
                                />
                            </DisplayBox>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Leigusamningar;